// Component Editor Registry
// Auto-discovers component editors from both aldoo-ra and project folders

// Auto-discover default component editors
const defaultEditors = import.meta.glob('./*.jsx', {
  eager: true,
})

// Auto-discover project-specific component editors
const projectEditors = import.meta.glob('/components/Structure/component-editors/*.jsx', {
  eager: true,
})

class ComponentEditorRegistry {
  static #editorCache = new Map()
  static initialized = false

  static init() {
    if (this.initialized) return

    // Register default editors from aldoo-ra
    Object.entries(defaultEditors).forEach(([path, module]) => {
      if (module.default && module.editorFor) {
        // Editor specifies which component it's for
        this.#editorCache.set(module.editorFor.toLowerCase(), module.default)
      } else if (module.default) {
        // Extract editor name from filename
        const fileName = path.split('/').pop().replace('.jsx', '')
        this.#editorCache.set(fileName.toLowerCase(), module.default)
      }
    })

    // Register project-specific editors (override defaults if same name)
    Object.entries(projectEditors).forEach(([path, module]) => {
      if (module.default && module.editorFor) {
        this.#editorCache.set(module.editorFor.toLowerCase(), module.default)
      } else if (module.default) {
        const fileName = path.split('/').pop().replace('.jsx', '')
        this.#editorCache.set(fileName.toLowerCase(), module.default)
      }
    })

    this.initialized = true
  }

  /**
   * Get editor for a component ID
   * Normalizes the component ID by converting to lowercase and replacing special characters with hyphens
   * @param {string} componentId - Component ID (e.g., "HTMLContent")
   * @returns {React.Component|null} - Editor component or null if not found
   */
  static getEditor(componentId) {
    if (!this.initialized) {
      this.init()
    }

    if (!componentId) return null

    // Normalize component ID: lowercase and replace spaces/special chars with hyphens
    const normalizedId = componentId
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens

    return this.#editorCache.get(normalizedId) || null
  }

  /**
   * Check if a custom editor exists for a component
   * @param {string} componentId - Component ID
   * @returns {boolean}
   */
  static hasEditor(componentId) {
    return this.getEditor(componentId) !== null
  }

  /**
   * Get all registered editors
   * @returns {Map}
   */
  static getRegistry() {
    if (!this.initialized) {
      this.init()
    }
    return this.#editorCache
  }

  /**
   * Clear the registry (for testing)
   */
  static clear() {
    this.#editorCache.clear()
    this.initialized = false
  }
}

export default ComponentEditorRegistry
